import { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FiAlertCircle,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiDollarSign,
  FiDownload,
  FiFilter,
  FiSearch,
  FiTrendingUp
} from "react-icons/fi";
import "../../../assets/css/doctor/doctorpages/Wallet.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import { AppContext } from "../../../context/AppContext";

const Wallet = () => {
  const { baseUrl } = useContext(AppContext);
  const token = localStorage.getItem("doctor_token");
  const authHeaders = { Authorization: `Bearer ${token}`, Accept: "application/json" };

  const [selectedFilter, setSelectedFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawing, setWithdrawing] = useState(false);

  const [walletStats, setWalletStats] = useState({
    totalBalance: 0,
    totalEarned: 0,
    pendingWithdrawals: 0,
    totalWithdrawn: 0,
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Fetch summary + transactions ────────────────────────────
  const fetchWalletData = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${baseUrl}/doctor/Wallet/summary`, { headers: authHeaders }),
      axios.get(`${baseUrl}/doctor/Wallet/transactions`, { headers: authHeaders }),
    ])
      .then(([summaryRes, txRes]) => {
        setWalletStats({
          totalBalance: parseFloat(summaryRes.data.wallet_balance) || 0,
          totalEarned: parseFloat(summaryRes.data.total_earned) || 0,
          pendingWithdrawals: parseFloat(summaryRes.data.pending_withdrawals) || 0,
          totalWithdrawn: parseFloat(summaryRes.data.total_withdrawn) || 0,
        });
        setTransactions(txRes.data.data || []);
      })
      .catch((err) => {
        console.error("Wallet load error:", err);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  // ── Withdraw ─────────────────────────────────────────────────
  const handleWithdraw = () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount <= 0) {
      Swal.fire({ icon: "warning", title: "Invalid Amount", text: "Please enter a valid amount." });
      return;
    }
    if (amount > walletStats.totalBalance) {
      Swal.fire({ icon: "error", title: "Insufficient Balance", text: "You don't have enough balance for this withdrawal." });
      return;
    }

    setWithdrawing(true);
    axios.post(`${baseUrl}/doctor/Wallet/withdraw`, { amount }, { headers: authHeaders })
      .then((res) => {
        Swal.fire({
          icon: "success",
          title: "Request Submitted",
          text: res.data.message,
          timer: 3000,
          showConfirmButton: false,
        });
        setShowWithdrawModal(false);
        setWithdrawAmount("");
        fetchWalletData();
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: err?.response?.data?.message || "Something went wrong.",
        });
      })
      .finally(() => setWithdrawing(false));
  };

  // ── Filtering ────────────────────────────────────────────────
  // Backend types: credit, debit, withdrawal. UI groups "withdrawal" under "debit".
  const uiType = (type) => (type === "withdrawal" ? "debit" : type);

  const filteredTransactions = transactions.filter((transaction) => {
    const matchesFilter =
      selectedFilter === "all" ||
      uiType(transaction.type) === selectedFilter ||
      transaction.status === selectedFilter;
    const matchesSearch =
      (transaction.description || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(transaction.id).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "completed":
        return "wlt-status-completed";
      case "pending":
        return "wlt-status-pending";
      case "failed":
        return "wlt-status-failed";
      default:
        return "";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <FiCheckCircle />;
      case "pending":
        return <FiClock />;
      case "failed":
        return <FiAlertCircle />;
      default:
        return null;
    }
  };

  const handleExport = () => {
    if (filteredTransactions.length === 0) return;
    const rows = [
      ["Transaction ID", "Description", "Date", "Type", "Amount", "Status"],
      ...filteredTransactions.map((t) => [
        t.id,
        t.description || "",
        formatDate(t.created_at),
        uiType(t.type),
        t.amount,
        t.status,
      ]),
    ];
    const csvContent = rows.map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "wallet_transactions.csv";
    link.click();
  };

  return (
    <DoctorMasterLayout>
    <div>
      <div className="wlt-container">
        <div className="wlt-content">
          {/* Header */}
          <div className="wlt-header">
            <div className="wlt-header-left">
              <h2>My Wallet</h2>
              <p>Manage your earnings and transactions</p>
            </div>
            <div className="wlt-header-right">
              <button className="wlt-btn-withdraw" onClick={() => setShowWithdrawModal(true)}>
                <FiArrowUpRight /> Withdraw Funds
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="wlt-stats-grid">
            <div className="wlt-stat-card balance">
              <div className="wlt-stat-icon">
                <FiDollarSign />
              </div>
              <div className="wlt-stat-content">
                <p className="wlt-stat-label">Available Balance</p>
                <h3 className="wlt-stat-value">{loading ? "..." : formatCurrency(walletStats.totalBalance)}</h3>
              </div>
            </div>

            <div className="wlt-stat-card earning">
              <div className="wlt-stat-icon">
                <FiTrendingUp />
              </div>
              <div className="wlt-stat-content">
                <p className="wlt-stat-label">Total Earned</p>
                <h3 className="wlt-stat-value">{loading ? "..." : formatCurrency(walletStats.totalEarned)}</h3>
              </div>
            </div>

            <div className="wlt-stat-card pending">
              <div className="wlt-stat-icon">
                <FiClock />
              </div>
              <div className="wlt-stat-content">
                <p className="wlt-stat-label">Pending Withdrawals</p>
                <h3 className="wlt-stat-value">{loading ? "..." : formatCurrency(walletStats.pendingWithdrawals)}</h3>
                <span className="wlt-stat-info">
                  <FiCalendar /> Processing in 2-3 days
                </span>
              </div>
            </div>

            <div className="wlt-stat-card withdrawn">
              <div className="wlt-stat-icon">
                <FiCreditCard />
              </div>
              <div className="wlt-stat-content">
                <p className="wlt-stat-label">Total Withdrawn</p>
                <h3 className="wlt-stat-value">{loading ? "..." : formatCurrency(walletStats.totalWithdrawn)}</h3>
                <span className="wlt-stat-info">
                  <FiCheckCircle /> Lifetime withdrawals
                </span>
              </div>
            </div>
          </div>

          {/* Transactions Section */}
          <div className="wlt-transactions-section">
            <div className="wlt-transactions-header">
              <h3>Transaction History</h3>
              <div className="wlt-transactions-actions">
                <div className="wlt-search-box">
                  <FiSearch />
                  <input
                    type="text"
                    placeholder="Search transactions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="wlt-filter-buttons">
                  <button
                    className={`wlt-filter-btn ${selectedFilter === "all" ? "active" : ""}`}
                    onClick={() => setSelectedFilter("all")}
                  >
                    <FiFilter /> All
                  </button>
                  <button
                    className={`wlt-filter-btn ${selectedFilter === "credit" ? "active" : ""}`}
                    onClick={() => setSelectedFilter("credit")}
                  >
                    <FiArrowDownLeft /> Credit
                  </button>
                  <button
                    className={`wlt-filter-btn ${selectedFilter === "debit" ? "active" : ""}`}
                    onClick={() => setSelectedFilter("debit")}
                  >
                    <FiArrowUpRight /> Debit
                  </button>
                </div>
                <button className="wlt-btn-export" onClick={handleExport}>
                  <FiDownload /> Export
                </button>
              </div>
            </div>

            {/* Transactions Table */}
            <div className="wlt-transactions-table">
              {loading ? (
                <div className="wlt-no-transactions">
                  <p>Loading transactions...</p>
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Transaction ID</th>
                      <th>Description</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>
                          <span className="wlt-transaction-id">TXN{String(transaction.id).padStart(4, "0")}</span>
                        </td>
                        <td>
                          <div className="wlt-transaction-description">
                            <span className="wlt-description-text">
                              {transaction.description ||
                                (transaction.type === "withdrawal" ? "Withdrawal Request" : "Session Payment")}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="wlt-transaction-date">
                            <FiCalendar /> {formatDate(transaction.created_at)}
                          </span>
                        </td>
                        <td>
                          <span className={`wlt-transaction-type ${uiType(transaction.type)}`}>
                            {uiType(transaction.type) === "credit" ? (
                              <><FiArrowDownLeft /> Credit</>
                            ) : (
                              <><FiArrowUpRight /> Debit</>
                            )}
                          </span>
                        </td>
                        <td>
                          <span className={`wlt-transaction-amount ${uiType(transaction.type)}`}>
                            {uiType(transaction.type) === "credit" ? "+" : "-"} {formatCurrency(transaction.amount)}
                          </span>
                        </td>
                        <td>
                          <span className={`wlt-transaction-status ${getStatusClass(transaction.status)}`}>
                            {getStatusIcon(transaction.status)} {transaction.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {!loading && filteredTransactions.length === 0 && (
                <div className="wlt-no-transactions">
                  <FiAlertCircle />
                  <p>No transactions found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <div className="wlt-modal-overlay" onClick={() => !withdrawing && setShowWithdrawModal(false)}>
          <div className="wlt-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="wlt-modal-header">
              <h3>Withdraw Funds</h3>
              <button className="wlt-modal-close" onClick={() => setShowWithdrawModal(false)}>
                ×
              </button>
            </div>
            <div className="wlt-modal-body">
              <div className="wlt-available-balance">
                <p>Available Balance</p>
                <h4>{formatCurrency(walletStats.totalBalance)}</h4>
              </div>
              <div className="wlt-form-group">
                <label htmlFor="withdrawAmount">
                  <FiDollarSign /> Enter Amount (PKR)
                </label>
                <input
                  type="number"
                  id="withdrawAmount"
                  placeholder="Enter amount to withdraw"
                  value={withdrawAmount}
                  onChange={(e) => setWithdrawAmount(e.target.value)}
                />
              </div>
              <div className="wlt-withdraw-info">
                <FiAlertCircle />
                <p>Withdrawal will be processed within 2-3 business days to your registered bank account.</p>
              </div>
            </div>
            <div className="wlt-modal-footer">
              <button className="wlt-btn-cancel" onClick={() => setShowWithdrawModal(false)} disabled={withdrawing}>
                Cancel
              </button>
              <button className="wlt-btn-confirm" onClick={handleWithdraw} disabled={withdrawing}>
                <FiCheckCircle /> {withdrawing ? "Processing..." : "Confirm Withdrawal"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </DoctorMasterLayout>
  );
};

export default Wallet;